import { createSuspenseQuery } from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";
import { ChecklistRelationSchema } from "../../schemas/checklist.js";

const EntityTypeSchema = z.enum(["client", "resource", "task"]);
export type ChecklistEntityType = z.infer<typeof EntityTypeSchema>;

const BaseRequestSchema = z.object({
  id: z.int().positive(),
  api: z.enum(["wf", "public"]),
  entity_type: EntityTypeSchema
});

const RequestSchema = z.discriminatedUnion("api", [
  BaseRequestSchema.extend({
    api: z.literal("wf")
  }),
  BaseRequestSchema.extend({
    api: z.literal("public"),
    slug: z.string()
  })
]);

const RequestVariables = z.union([
  BaseRequestSchema.extend({ api: z.literal("wf") }).omit({ api: true }),
  BaseRequestSchema.extend({ api: z.literal("public"), slug: z.string() }).omit(
    { api: true }
  )
]);

const ResponseSchema = z.array(ChecklistRelationSchema);

type Response = z.infer<typeof ResponseSchema>;
type Request = z.infer<typeof RequestSchema>;

export type EntityChecklistsProps = Request;

const urls = {
  wf: {
    task: "/task/:id/checklists",
    resource: "/resource/:id/checklists",
    client: "/client/:id/checklists"
  },
  public: {
    task: "/public/:slug/task/:id/checklists",
    resource: "/public/:slug/resource/:id/checklists",
    client: "/public/:slug/client/:id/checklists"
  }
} as const;

export const useEntityChecklistsSuspense = (entity_type: ChecklistEntityType) =>
  createSuspenseQuery<Response, Request, Error>({
    queryKey: [`entity${entity_type}ChecklistsWithSuspense`],
    staleTime: 1000 * 60,
    fetcher: async (body) => {
      const { api: apiType, ...rest } = body;
      const result = await api<Omit<Request, "api">, Response>({
        method: "GET",
        url: urls[apiType][rest.entity_type],
        requestSchema: RequestVariables,
        responseSchema: ResponseSchema,
        body: rest
      });
      return result;
    }
  });
